<template>
  <div class="dashboard-list">
    <!-- Header -->
    <v-row class="mb-4">
      <v-col cols="12">
        <div class="d-flex justify-space-between align-center">
          <div>
            <h1 class="text-h4 font-weight-bold">My Dashboards</h1>
            <p class="text-subtitle-1 text--secondary mb-0">
              Manage and organize your custom dashboards
            </p>
          </div>
          <div>
            <v-btn
              color="primary"
              @click="showCreateDialog = true"
              :disabled="dashboardLoading"
            >
              <v-icon left>mdi-plus</v-icon>
              New Dashboard
            </v-btn>
          </div>
        </div>
      </v-col>
    </v-row>

    <!-- Loading State -->
    <div v-if="dashboardLoading" class="loading-container">
      <v-row justify="center" class="mt-12">
        <v-col cols="12" class="text-center">
          <v-progress-circular
            indeterminate
            size="64"
            width="4"
            color="primary"
          ></v-progress-circular>
          <h3 class="text-h5 mt-4">Loading Dashboards...</h3>
        </v-col>
      </v-row>
    </div>

    <!-- Error State -->
    <div v-else-if="dashboardError" class="error-container">
      <v-row justify="center" class="mt-12">
        <v-col cols="12" md="6" class="text-center">
          <v-icon size="64" color="error">mdi-alert-circle-outline</v-icon>
          <h3 class="text-h5 mt-4">Error Loading Dashboards</h3>
          <p class="text-body-1 mb-4">{{ dashboardError }}</p>
          <v-btn color="primary" @click="loadDashboards">
            <v-icon left>mdi-refresh</v-icon>
            Retry
          </v-btn>
        </v-col>
      </v-row>
    </div>

    <!-- Dashboard Grid -->
    <div v-else class="dashboard-grid">
      <v-row>
        <v-col
          v-for="dashboard in userDashboards"
          :key="dashboard.dashboardId"
          cols="12"
          sm="6"
          md="4"
          lg="4"
        >
          <v-card
            class="dashboard-card"
            :class="{
              'current-dashboard': dashboard.dashboardId === currentDashboardId,
            }"
            @click="openDashboard(dashboard.dashboardId)"
            hover
          >
            <v-card-title class="pb-2" style="width: 300px">
              <div class="justify-space-between align-center">
                <span class="dashboard-name">{{ dashboard.name }}</span>
                <v-menu offset-y>
                  <template v-slot:activator="{ on, attrs }">
                    <v-btn icon small v-bind="attrs" v-on="on" @click.stop>
                      <v-icon>mdi-dots-vertical</v-icon>
                    </v-btn>
                  </template>
                  <v-list dense>
                    <v-list-item @click="editDashboard(dashboard)">
                      <v-list-item-icon>
                        <v-icon>mdi-pencil</v-icon>
                      </v-list-item-icon>
                      <v-list-item-content>
                        <v-list-item-title>Edit</v-list-item-title>
                      </v-list-item-content>
                    </v-list-item>
                    <v-list-item @click="duplicateDashboard(dashboard)">
                      <v-list-item-icon>
                        <v-icon>mdi-content-copy</v-icon>
                      </v-list-item-icon>
                      <v-list-item-content>
                        <v-list-item-title>Duplicate</v-list-item-title>
                      </v-list-item-content>
                    </v-list-item>
                    <v-divider></v-divider>
                    <v-list-item @click="confirmDelete(dashboard)">
                      <v-list-item-icon>
                        <v-icon color="error">mdi-delete</v-icon>
                      </v-list-item-icon>
                      <v-list-item-content>
                        <v-list-item-title class="error--text"
                          >Delete</v-list-item-title
                        >
                      </v-list-item-content>
                    </v-list-item>
                  </v-list>
                </v-menu>
              </div>
            </v-card-title>

            <v-card-subtitle v-if="dashboard.description" class="pb-2 mt-1">
              {{ dashboard.description }}
            </v-card-subtitle>

            <v-card-text>
              <div class="dashboard-stats">
                <div class="stat-item">
                  <v-icon small color="primary">mdi-widgets</v-icon>
                  <span class="ml-1"
                    >{{
                      dashboard.widgets ? dashboard.widgets.length : 0
                    }}
                    widgets</span
                  >
                </div>
                <div class="stat-item">
                  <v-icon small color="grey">mdi-clock-outline</v-icon>
                  <span class="ml-1">{{
                    formatDate(dashboard.updatedAt)
                  }}</span>
                </div>
              </div>
            </v-card-text>

            <v-card-actions v-if="dashboard.dashboardId === currentDashboardId">
              <v-chip small color="primary" outlined>
                <v-icon left small>mdi-eye</v-icon>
                Currently Viewing
              </v-chip>
            </v-card-actions>
          </v-card>
        </v-col>
      </v-row>
    </div>

    <!-- Create Dashboard Dialog -->
    <v-dialog v-model="showCreateDialog" max-width="500" persistent>
      <v-card>
        <v-card-title>
          <span class="text-h6">Create New Dashboard</span>
        </v-card-title>
        <v-card-text>
          <v-text-field
            v-model="newDashboard.name"
            label="Dashboard Name"
            outlined
            dense
            :rules="[(v) => !!v || 'Name is required']"
            ref="nameInput"
          ></v-text-field>
          <v-textarea
            v-model="newDashboard.description"
            label="Description (optional)"
            outlined
            dense
            rows="3"
          ></v-textarea>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="cancelCreate">Cancel</v-btn>
          <v-btn
            color="primary"
            @click="createDashboard"
            :disabled="!newDashboard.name"
            :loading="creating"
          >
            Create
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Edit Dashboard Dialog -->
    <v-dialog v-model="showEditDialog" max-width="500" persistent>
      <v-card>
        <v-card-title>
          <span class="text-h6">Edit Dashboard</span>
        </v-card-title>
        <v-card-text>
          <v-text-field
            v-model="editingDashboard.name"
            label="Dashboard Name"
            outlined
            dense
            :rules="[(v) => !!v || 'Name is required']"
          ></v-text-field>
          <v-textarea
            v-model="editingDashboard.description"
            label="Description (optional)"
            outlined
            dense
            rows="3"
          ></v-textarea>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="cancelEdit">Cancel</v-btn>
          <v-btn
            color="primary"
            @click="saveEdit"
            :disabled="!editingDashboard.name"
            :loading="editing"
          >
            Save
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Delete Confirmation Dialog -->
    <v-dialog v-model="showDeleteDialog" max-width="400">
      <v-card>
        <v-card-title class="text-h6"> Delete Dashboard </v-card-title>
        <v-card-text>
          Are you sure you want to delete "<strong>{{
            deletingDashboard && deletingDashboard.name
              ? deletingDashboard.name
              : "this dashboard"
          }}</strong
          >"? This action cannot be undone.
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="showDeleteDialog = false">Cancel</v-btn>
          <v-btn color="error" @click="deleteDashboard" :loading="deleting">
            Delete
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
export default {
  name: "DashboardList",
  data() {
    return {
      showCreateDialog: false,
      showEditDialog: false,
      showDeleteDialog: false,
      creating: false,
      editing: false,
      deleting: false,
      newDashboard: {
        name: "",
        description: "",
      },
      editingDashboard: {
        dashboardId: null,
        name: "",
        description: "",
      },
      deletingDashboard: null,
    };
  },
  computed: {
    userDashboards() {
      return this.$store.getters.userDashboards;
    },
    currentDashboardId() {
      return this.$store.getters.currentDashboardId;
    },
    dashboardLoading() {
      return this.$store.getters.dashboardLoading;
    },
    dashboardError() {
      return this.$store.getters.dashboardError;
    },
  },
  async mounted() {
    await this.loadDashboards();
  },
  methods: {
    async loadDashboards() {
      try {
        await this.$store.dispatch("loadUserDashboards");
      } catch (error) {
        console.error("Error loading dashboards:", error);
      }
    },
    openDashboard(dashboardId) {
      this.$router.push(`/reports/${dashboardId}`);
    },
    editDashboard(dashboard) {
      this.editingDashboard = {
        dashboardId: dashboard.dashboardId,
        name: dashboard.name,
        description: dashboard.description || "",
      };
      this.showEditDialog = true;
    },
    async duplicateDashboard(dashboard) {
      try {
        const newName = `${dashboard.name} (Copy)`;
        await this.$store.dispatch("duplicateDashboard", {
          dashboardId: dashboard.dashboardId,
          newName,
        });
        console.log("Dashboard duplicated successfully");
      } catch (error) {
        console.error("Failed to duplicate dashboard:", error);
      }
    },
    confirmDelete(dashboard) {
      this.deletingDashboard = dashboard;
      this.showDeleteDialog = true;
    },
    async createDashboard() {
      this.creating = true;
      try {
        const newDashboard = await this.$store.dispatch("createDashboard", {
          name: this.newDashboard.name,
          description: this.newDashboard.description,
        });
        this.showCreateDialog = false;
        this.newDashboard = { name: "", description: "" };
        console.log("Dashboard created successfully");

        // Refresh the dashboard list to ensure sidebar updates
        await this.$store.dispatch("loadUserDashboards");

        // Navigate to the new dashboard
        this.$router.push(`/reports/${newDashboard.dashboardId}`);
      } catch (error) {
        console.error("Failed to create dashboard:", error);
      } finally {
        this.creating = false;
      }
    },
    async saveEdit() {
      this.editing = true;
      try {
        await this.$store.dispatch("updateDashboardInfo", {
          dashboardId: this.editingDashboard.dashboardId,
          name: this.editingDashboard.name,
          description: this.editingDashboard.description,
        });
        this.showEditDialog = false;
        console.log("Dashboard updated successfully");
      } catch (error) {
        console.error("Failed to update dashboard:", error);
      } finally {
        this.editing = false;
      }
    },
    async deleteDashboard() {
      this.deleting = true;
      try {
        await this.$store.dispatch(
          "deleteDashboard",
          this.deletingDashboard.dashboardId
        );
        this.showDeleteDialog = false;
        this.deletingDashboard = null;
        console.log("Dashboard deleted successfully");

        // Refresh the dashboard list to ensure UI updates
        await this.$store.dispatch("loadUserDashboards");
      } catch (error) {
        console.error("Failed to delete dashboard:", error);
      } finally {
        this.deleting = false;
      }
    },
    cancelCreate() {
      this.showCreateDialog = false;
      this.newDashboard = { name: "", description: "" };
    },
    cancelEdit() {
      this.showEditDialog = false;
      this.editingDashboard = { dashboardId: null, name: "", description: "" };
    },
    formatDate(dateString) {
      if (!dateString) return "Unknown";
      const date = new Date(dateString);
      const now = new Date();
      const diffTime = Math.abs(now - date);
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

      if (diffDays === 1) return "Today";
      if (diffDays === 2) return "Yesterday";
      if (diffDays <= 7) return `${diffDays - 1} days ago`;

      return date.toLocaleDateString();
    },
  },
};
</script>

<style scoped>
.dashboard-list {
  padding: 24px;
  min-height: 100vh;
}

.dashboard-grid {
  margin-top: 24px;
}

.dashboard-card {
  height: 100%;
  cursor: pointer;
  transition: all 0.2s ease;
  border: 2px solid transparent;
}

.dashboard-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.12);
}

.current-dashboard {
  border-color: #2196f3;
  background: linear-gradient(
    135deg,
    rgba(33, 150, 243, 0.05) 0%,
    rgba(33, 150, 243, 0.02) 100%
  );
}

.dashboard-name {
  font-weight: 600;
  font-size: 1.1rem;
  line-height: 1.2;
  max-width: 200px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.dashboard-stats {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.stat-item {
  display: flex;
  align-items: center;
  font-size: 0.875rem;
  color: #666;
}

.loading-container,
.error-container {
  min-height: 400px;
  display: flex;
  align-items: center;
  justify-content: center;
}

@media (max-width: 960px) {
  .dashboard-list {
    padding: 16px;
  }

  .dashboard-name {
    max-width: 100% !important;
  }
}
</style>
